import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  VStack,
  HStack,
    FormControl,
  FormLabel,
  FormErrorMessage,
  Card,
  Badge,
  Icon,
  useToast,
} from '@chakra-ui/react';
import ReactQuill from 'react-quill';
import CustomSelect from 'components/select/CustomSelect';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaEdit } from 'react-icons/fa';
import { useCreatePolicyMutation } from 'api/policiesSlice';
const AddPrivacyAndTerms = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // API hook
  const [createPolicy, { isLoading }] = useCreatePolicyMutation();

  const [formData, setFormData] = React.useState({
    type: '',
    content: '',
    status: 'active',
  });

  const [errors, setErrors] = React.useState({});

  // Register custom line-height attribute
  React.useEffect(() => {
    try {
      const LineHeightStyle = Quill.import('attributors/style/line-height');
      if (LineHeightStyle) {
        LineHeightStyle.whitelist = ['1', '1.2', '1.4', '1.6', '1.8', '2'];
        Quill.register(LineHeightStyle, true);
      }
    } catch (error) {
      console.log('Line-height attribute registration skipped:', error);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Prepare the data according to the API structure
      const policyData = {
        type: formData.type,
        content: formData.content,
        status: formData.status,
      };

      await createPolicy(policyData).unwrap();

      toast({
        title: 'Success',
        description: 'Policy created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/admin/undefined/privacy-and-terms');
      
    } catch (error) {
      console.error('Failed to create policy:', error);
      toast({
        title: 'Error',
        description: error.data?.message || 'Failed to create policy. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const renderFormContent = () => {
    return (
      <VStack spacing={6} align="stretch" w="100%">
        <FormControl isInvalid={!!errors.type}>
          <FormLabel color={textColor} fontWeight="bold">
            Policy Type *
          </FormLabel>
          <CustomSelect
            placeholder="Select policy type"
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
          >
            <option value="privacy">Privacy Policy</option>
            <option value="terms">Terms & Conditions</option>
          </CustomSelect>
          <FormErrorMessage>{errors.type}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel color={textColor} fontWeight="bold">
            Status
          </FormLabel>
          <CustomSelect
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </CustomSelect>
        </FormControl>
        
        <FormControl isInvalid={!!errors.content}>
          <FormLabel color={textColor} fontWeight="bold">
            Policy Content *
          </FormLabel>
          <Box border="1px" borderColor={borderColor} borderRadius="md">
            <style>
              {`
                .ql-editor {
                  line-height: 1.6 !important;
                }
                .ql-editor p {
                  margin-bottom: 1em;
                }
                .ql-editor h1, .ql-editor h2, .ql-editor h3, .ql-editor h4, .ql-editor h5, .ql-editor h6 {
                  margin-top: 1em;
                  margin-bottom: 0.5em;
                }
              `}
            </style>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={(value) => handleInputChange('content', value)}
              placeholder="Enter the detailed policy content..."
              formats={[
                'header', 'bold', 'italic', 'underline', 'strike',
                'list', 'bullet', 'indent',
                'color', 'background', 'align', 'lineHeight',
                'code-block', 'blockquote', 'code',
                'link', 'image', 'clean'
              ]}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'indent': '-1'}, { 'indent': '+1' }],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'align': [] }],
                  [{ 'lineHeight': ['1', '1.2', '1.4', '1.6', '1.8', '2'] }],
                  ['blockquote', 'code-block'],
                  ['link', 'image'],
                  ['clean']
                ],
                clipboard: {
                  matchVisual: false,
                }
              }}
              style={{ 
                height: '350px',
                marginBottom: '50px'
              }}
            />
          </Box>
          <FormErrorMessage>{errors.content}</FormErrorMessage>
        </FormControl>
      </VStack>
    );
  };

  return (
    <Box className="nr-page">
      <Card p={6}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Text color={textColor} fontSize="2xl" fontWeight="bold" mb={2}>
              Add New Policy
            </Text>
            <Text color="gray.500">
              Create a new privacy policy or terms of service document
            </Text>
          </Box>

          <Box p={6} border="1px solid" borderColor={borderColor} borderRadius="lg">
            {renderFormContent()}
          </Box>

          <HStack justify="space-between">
            <Button
              onClick={() => navigate('/admin/undefined/privacy-and-terms')}
              variant="outline"
              colorScheme="gray"
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              colorScheme="green"
              isLoading={isLoading}
              loadingText="Creating Policy"
            >
              Create Policy
            </Button>
          </HStack>
        </VStack>
      </Card>
    </Box>
  );
};

export default AddPrivacyAndTerms;
